import type { Result } from 'true-myth';

import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { LanguageCode } from '~/shared/internationalization';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    AutomationDetailResponse,
    AutomationHandoverResponse,
    AutomationLayer,
    AutomationPointsResponse,
    AutomationRankingResponse,
    AutomationRankingSort,
    AutomationSearchResponse,
} from '../../data-contract/automation';
import {
    automationDetailResponseSchema,
    automationHandoverResponseSchema,
    automationPointsResponseSchema,
    automationRankingResponseSchema,
    automationSearchResponseSchema,
} from '../../data-contract/automation';

type RequestContext = {
    readonly acceptLanguage?: LanguageCode;
    readonly signal?: AbortSignal;
};

export type FetchAutomationPointsInput = RequestContext & {
    readonly architectureId: string;
    readonly layer: AutomationLayer;
    readonly parentId?: string;
};

export type SearchAutomationPointsInput = RequestContext & {
    readonly architectureId: string;
    readonly layer: AutomationLayer;
    readonly limit?: number;
    readonly query: string;
};

export type FetchAutomationDetailInput = RequestContext & {
    readonly architectureId: string;
    readonly itemId: string;
};

export type FetchAutomationHandoverInput = RequestContext & {
    readonly architectureId: string;
    readonly layer: AutomationLayer;
    readonly parentId?: string;
};

export type FetchAutomationRankingInput = RequestContext & {
    readonly architectureId: string;
    readonly layer: AutomationLayer;
    readonly limit?: number;
    readonly sortBy?: AutomationRankingSort;
};

export type AutomationFetchers = {
    readonly fetchAutomationDetail: (
        input: FetchAutomationDetailInput,
    ) => Promise<Result<AutomationDetailResponse, HttpFetcherError>>;
    readonly fetchAutomationHandover: (
        input: FetchAutomationHandoverInput,
    ) => Promise<Result<AutomationHandoverResponse, HttpFetcherError>>;
    readonly fetchAutomationPoints: (
        input: FetchAutomationPointsInput,
    ) => Promise<Result<AutomationPointsResponse, HttpFetcherError>>;
    readonly fetchAutomationRanking: (
        input: FetchAutomationRankingInput,
    ) => Promise<Result<AutomationRankingResponse, HttpFetcherError>>;
    readonly searchAutomationPoints: (
        input: SearchAutomationPointsInput,
    ) => Promise<Result<AutomationSearchResponse, HttpFetcherError>>;
};

export type AutomationFetchersDependencies = {
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

const requestOptions = ({ acceptLanguage, signal }: RequestContext) => ({
    headers: acceptLanguage === undefined ? undefined : { 'Accept-Language': acceptLanguage },
    signal,
});

export const createAutomationFetchers = ({
    validatingCobrainerApiFetcher,
}: AutomationFetchersDependencies): AutomationFetchers => ({
    fetchAutomationDetail: ({ architectureId, itemId, ...context }) =>
        validatingCobrainerApiFetcher.get(automationDetailResponseSchema, {
            pathname: `/job-architectures/${architectureId}/items/${itemId}/automation-detail`,
            requestOptions: requestOptions(context),
        }),
    fetchAutomationHandover: ({ architectureId, layer, parentId, ...context }) =>
        validatingCobrainerApiFetcher.get(automationHandoverResponseSchema, {
            pathname: `/job-architectures/${architectureId}/automation-handover`,
            requestOptions: { ...requestOptions(context), searchParams: { layer, parentId } },
        }),
    fetchAutomationPoints: ({ architectureId, layer, parentId, ...context }) =>
        validatingCobrainerApiFetcher.get(automationPointsResponseSchema, {
            pathname: `/job-architectures/${architectureId}/automation-points`,
            requestOptions: { ...requestOptions(context), searchParams: { layer, parentId } },
        }),
    fetchAutomationRanking: ({ architectureId, layer, limit, sortBy, ...context }) =>
        validatingCobrainerApiFetcher.get(automationRankingResponseSchema, {
            pathname: `/job-architectures/${architectureId}/automation-points/ranking`,
            requestOptions: { ...requestOptions(context), searchParams: { layer, limit, sortBy } },
        }),
    searchAutomationPoints: ({ architectureId, layer, limit, query, ...context }) =>
        validatingCobrainerApiFetcher.get(automationSearchResponseSchema, {
            pathname: `/job-architectures/${architectureId}/automation-points/search`,
            requestOptions: { ...requestOptions(context), searchParams: { layer, limit, q: query } },
        }),
});
